import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { DatePicker, Select, Space, Typography } from 'antd';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { LineChart } from 'components/Custom/lineChart';
import { FilterSelect, FormSelect } from 'components/Custom/selects';
import { ScopeTable } from 'components/Custom/table';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { applyFilters as applyFiltersPipeline } from 'filter-system';
import dayjs from 'dayjs';
import {
  formatDisplayDate,
  formatDisplayDateRange,
  getLocalizedDatePickerFormat,
} from 'i18n/calendarLabels';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DateOptions } from 'store/api/enums';
import { DataLogStatus } from 'store/app/enums';
import { User } from 'store/auth/models';
import { getReturnTypeNameLabel } from 'i18n/returnTypeLabel';
import { clearOrganisationDetailsTabStorage } from 'utils/clear-organisation-tab-storage';
import { getDateOptionLabel } from 'utils/display-enum-label';
import { getProviderLink } from 'utils/get-provider-link';
import { getReturnActionLabel } from 'utils/return-action-label';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import { BaseCard } from './BaseCard';
import { SubmittedReturnsChartCardProps } from './CardProps';
import { DASHBOARD_CARD_TITLE_KEYS } from './dashboardCardKeys';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const dateFormat = 'DD/MM/YYYY';
const DROPDOWN_OPEN_GUARD_MS = 250;

export const SubmittedReturnsChartCard: React.FC<
  SubmittedReturnsChartCardProps
> = ({
  cardId,
  submittedReturnsData,
  filteredSubmittedReturns,
  submittedDateRange,
  submittedDateRangeOption,
  enableCustomRange,
  submittedDateFilterOpen,
  setSubmittedDateRange,
  onSubmittedDateOptionSelect,
  onCustomRangeCheckboxChange,
  handleApplyDateFilter,
  handleCancelDateFilter,
  handleOpenDateFilterDropdown,
  openSubmission,
  capturedSubmissionsPageSize,
  saveCapturedSubmissionsPageSize,
  t,
}) => {
  const { i18n } = useTranslation();
  const dropdownOpenedAtRef = React.useRef(0);
  const [filtersState, setFiltersState] = React.useState<Record<string, any>>(
    {}
  );

  const submissionTableRows = React.useMemo(
    () =>
      filteredSubmittedReturns
        .map((ret, index) => {
          if (!ret || !ret.dataLog) return null;

          const log = ret.dataLog;
          const entityName = ret.organisation || t('common.unknown');
          const fullName = ret.mappingName;
          const parts = fullName.split(' - ');
          const returnName =
            parts.length > 1 ? parts.slice(1).join(' - ') : fullName;

          let submittedByUser: User | undefined;
          let submittedByName = t('common.unknown');

          if (log.preparerUser) {
            submittedByUser = log.preparerUser;
            submittedByName =
              `${log.preparerUser.firstName || ''} ${
                log.preparerUser.lastName || ''
              }`.trim() ||
              log.preparerUser.email ||
              t('common.unknown');
          } else if (log.reviewerUser) {
            submittedByUser = log.reviewerUser;
            submittedByName =
              `${log.reviewerUser.firstName || ''} ${
                log.reviewerUser.lastName || ''
              }`.trim() ||
              log.reviewerUser.email ||
              t('common.unknown');
          } else if (log.clientUser) {
            submittedByUser = log.clientUser;
            submittedByName =
              `${log.clientUser.firstName || ''} ${
                log.clientUser.lastName || ''
              }`.trim() ||
              log.clientUser.email ||
              t('common.unknown');
          } else if (log.submittedBy) {
            submittedByName = log.submittedBy;
          }

          let period = '';
          if (log.fromReportingDate && log.toReportingDate) {
            period = formatDisplayDateRange(
              log.fromReportingDate,
              log.toReportingDate,
              'DD/MM/YY',
              i18n.language
            );
          } else if (log.fromReportingDate) {
            period = formatDisplayDate(
              log.fromReportingDate,
              'DD/MM/YY',
              i18n.language
            );
          }

          const dataLog = {
            ...log,
            submittedByUser: submittedByUser ?? log.submittedByUser ?? null,
          };

          return {
            key: index,
            organisation: entityName,
            entityName,
            returnName,
            submittedByUser,
            submittedByName,
            submittedBy: submittedByUser
              ? [submittedByUser]
              : log.submittedBy
              ? [log.submittedBy]
              : [],
            dataLog,
            submittedDate: log.submissionDate
              ? formatDisplayDate(log.submissionDate, 'DD/MM/YYYY', i18n.language)
              : '',
            period,
            connectionId: ret.connectionId,
            mappingId: ret.mappingId,
            dataLogId: log.uuid,
            providerName: ret.providerName,
            shortCode: ret.shortCode,
            organisationId: ret.organisationId,
            log,
          };
        })
        .filter(Boolean),
    [filteredSubmittedReturns, t, i18n.language]
  );

  const displaySubmissionRows = React.useMemo(() => {
    if (!filtersState || Object.keys(filtersState).length === 0) {
      return submissionTableRows;
    }
    return (
      applyFiltersPipeline(submissionTableRows, filtersState) ??
      submissionTableRows
    );
  }, [submissionTableRows, filtersState]);

  const cardTitle = t(DASHBOARD_CARD_TITLE_KEYS[cardId]!);

  const renderSortIcon = ({ sortOrder }: { sortOrder?: string }) => {
    if (sortOrder === 'ascend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
    return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
  };

  const handleFilterOpenChange = (open: boolean) => {
    if (open) {
      dropdownOpenedAtRef.current = Date.now();
    } else if (
      Date.now() - dropdownOpenedAtRef.current <
      DROPDOWN_OPEN_GUARD_MS
    ) {
      return;
    }
    handleOpenDateFilterDropdown(open);
  };

  return (
    <BaseCard
      cardId={cardId}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text style={{ fontSize: '16px', fontWeight: 600 }}>{cardTitle}</Text>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Text
              style={{
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '12px',
                textAlign: 'left',
                color: 'var(--color-text-dark, #042a2a)',
                minWidth: 35,
              }}
            >
              {t('main.filters')}
            </Text>
            <div
              style={{
                position: 'relative',
                display: 'inline-block',
              }}
            >
              <style>{`
                .submitted-returns-date-filter-dropdown.ant-select-dropdown {
                  min-width: 380px !important;
                  width: max-content !important;
                  max-width: 90vw !important;
                  height: auto !important;
                  max-height: none !important;
                  overflow: visible !important;
                }
              `}</style>
              <FilterSelect
                placeholder={t('dashboard.submitted-date')}
                aria-label={t('dashboard.submitted-date')}
                getPopupContainer={() => document.body}
                placement="bottomLeft"
                popupMatchSelectWidth={false}
                classNames={{
                  popup: { root: 'submitted-returns-date-filter-dropdown' },
                }}
                styles={{
                  popup: {
                    root: {
                      minWidth: 380,
                      width: 'max-content',
                      maxWidth: '90vw',
                      height: 'auto',
                      maxHeight: 'none',
                      overflow: 'visible',
                      fontSize: 13,
                      borderRadius: 20,
                      zIndex: 1100,
                    },
                  },
                }}
                popupRender={() => (
                  <Space
                    className="dashboard-date-filter-panel"
                    direction="vertical"
                    style={{
                      padding: '20px',
                      width: '380px',
                      minWidth: '380px',
                      boxSizing: 'border-box',
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <FormSelect
                      className="dashboard-date-filter-form-select"
                      width={'100%'}
                      height={'35px'}
                      radius={'10px'}
                      isFilter={true}
                      placeholder={t('dashboard.submitted-date')}
                      aria-label={t('dashboard.submitted-date')}
                      getPopupContainer={() => document.body}
                      popupMatchSelectWidth={false}
                      style={{
                        backgroundColor: 'var(--color-surface, #F3F7F8)',
                        borderColor: '#cccccc',
                        fontSize: 13,
                      }}
                      styles={{
                        popup: { root: { fontSize: 13, zIndex: 1101 } },
                      }}
                      onSelect={onSubmittedDateOptionSelect}
                      value={submittedDateRangeOption}
                    >
                      {Object.values(DateOptions).map((value) => (
                        <Select.Option key={value} value={value}>
                          {getDateOptionLabel(value, t)}
                        </Select.Option>
                      ))}
                    </FormSelect>
                    <ScopeCheckbox
                      className="dashboard-date-filter-custom-range"
                      checked={enableCustomRange}
                      onChange={onCustomRangeCheckboxChange}
                    >
                      {t('main.custom-range')}
                    </ScopeCheckbox>
                    <RangePicker
                      className="dashboard-date-filter-range-picker"
                      style={{ borderRadius: '10px', height: '34px' }}
                      value={submittedDateRange}
                      disabled={!enableCustomRange}
                      format={getLocalizedDatePickerFormat(
                        dateFormat,
                        i18n.language
                      )}
                      getPopupContainer={() => document.body}
                      onChange={(dates) =>
                        setSubmittedDateRange(
                          dates as [dayjs.Dayjs | null, dayjs.Dayjs | null]
                        )
                      }
                    />
                    <Space
                      style={{
                        float: 'inline-end',
                        marginTop: '10px',
                        width: '100%',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <ScopeButton
                        width="75px"
                        height="25px"
                        fontSize="12px"
                        lineheight="12px"
                        onClick={handleCancelDateFilter}
                        color={'#41AAAF'}
                        type="default"
                      >
                        {t('common.cancel')}
                      </ScopeButton>
                      <ScopeButton
                        width="75px"
                        height="25px"
                        fontSize="12px"
                        lineheight="12px"
                        onClick={handleApplyDateFilter}
                        type="primary"
                      >
                        {t('common.apply')}
                      </ScopeButton>
                    </Space>
                  </Space>
                )}
                onOpenChange={handleFilterOpenChange}
                open={submittedDateFilterOpen}
                value={
                  submittedDateRangeOption
                    ? getDateOptionLabel(String(submittedDateRangeOption), t)
                    : undefined
                }
                className={submittedDateRangeOption ? 'selected-filter' : ''}
              >
                <Select.Option key="submitted-date">{''}</Select.Option>
              </FilterSelect>
            </div>
          </div>
        </div>
      }
      bodyStyle={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
      }}
    >
      {submittedReturnsData.length > 0 ? (
        <>
          <div
            style={{
              width: '100%',
              flexShrink: 0,
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            <LineChart
              data={submittedReturnsData}
              height={200}
              color="#41aaaf"
              width="100%"
            />
          </div>
          <div
            style={{
              marginTop: '24px',
              flex: 1,
              overflow: 'visible',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Text
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--color-text-dark, #042a2a)',
                marginBottom: '12px',
                display: 'block',
              }}
            >
              {t('dashboard.all-captured-submissions')}
            </Text>
            <div style={{ flex: 1, overflow: 'visible' }}>
              <ScopeTableFilters
                filters={['organisation', 'submittedBy']}
                value={filtersState}
                onChange={setFiltersState}
                originalDataSource={submissionTableRows}
                tableDataSource={displaySubmissionRows}
                embedded={false}
                filterRowMarginBottom={6}
              />
              <ScopeTable
                originalDataSource={submissionTableRows}
                tableDataSource={displaySubmissionRows}
                columns={[
                  {
                    title: t('dashboard.entity-name'),
                    dataIndex: 'organisation',
                    key: 'entityName',
                    sorter: (a: any, b: any) =>
                      (a.organisation || '').localeCompare(
                        b.organisation || ''
                      ),
                    sortIcon: renderSortIcon,
                    render: (text: string, record: any) => {
                      const to = record.organisationId
                        ? `/entities/organisation/${record.organisationId}`
                        : '#';
                      return (
                        <Space size={'small'} style={{ minWidth: '140px' }}>
                          {record.providerName ? (
                            <a
                              href={getProviderLink(
                                record.providerName.toLowerCase(),
                                record.shortCode
                              )}
                              target="_blank"
                              rel="noreferrer"
                              style={{ height: '32px', display: 'block' }}
                            >
                              <ProviderIcon
                                providerName={record.providerName}
                                alt={record.providerName}
                                width={25}
                                style={{ paddingTop: '4px' }}
                              />
                            </a>
                          ) : null}
                          <Link
                            to={to}
                            onClick={clearOrganisationDetailsTabStorage}
                          >
                            <Text
                              ellipsis={{ tooltip: true }}
                              style={{
                                fontSize: '12px',
                                maxWidth: 100,
                                color: 'var(--color-text-dark, #042a2a)',
                              }}
                            >
                              {text}
                            </Text>
                          </Link>
                        </Space>
                      );
                    },
                  },
                  {
                    title: t('dashboard.return-name'),
                    dataIndex: 'returnName',
                    key: 'returnName',
                    sorter: (a: any, b: any) =>
                      (a.returnName || '').localeCompare(b.returnName || ''),
                    sortIcon: renderSortIcon,
                    render: (text: string) => {
                      const label = getReturnTypeNameLabel(
                        text,
                        t,
                        i18n.language
                      );
                      return (
                      <Text
                        ellipsis={{ tooltip: label }}
                        style={{
                          fontSize: '12px',
                          maxWidth: 100,
                          color: 'var(--color-text-dark, #042a2a)',
                        }}
                      >
                        {label}
                      </Text>
                      );
                    },
                  },
                  {
                    title: t('main.submittedBy'),
                    dataIndex: 'submittedByName',
                    key: 'submittedBy',
                    sorter: (a: any, b: any) =>
                      (a.submittedByName || '').localeCompare(
                        b.submittedByName || ''
                      ),
                    sortIcon: renderSortIcon,
                    render: (text: string, record: any) => {
                      if (record.submittedByUser) {
                        const user = record.submittedByUser;
                        const abbreviatedFirstName = user.firstName
                          ? user.firstName
                              .trim()
                              .split(' ')
                              .map((name: string) => name.charAt(0) + '.')
                              .join('')
                          : '';
                        const fullName =
                          `${abbreviatedFirstName} ${
                            user.lastName || ''
                          }`.trim() ||
                          user.email ||
                          t('common.unknown');
                        return (
                          <Space size={'small'}>
                            {renderAvatar(
                              user.firstName,
                              user.lastName,
                              AvatarSizes.SMALL,
                              user.userUuid,
                              user.profilePicture
                            )}
                            <Text
                              style={{
                                fontSize: '12px',
                                color: 'var(--color-text-dark, #042a2a)',
                              }}
                              ellipsis={{ tooltip: true }}
                            >
                              {fullName}
                            </Text>
                          </Space>
                        );
                      } else {
                        return (
                          <Text style={{ fontSize: '12px', color: 'var(--color-text-dark, #042a2a)' }}>
                            {text}
                          </Text>
                        );
                      }
                    },
                  },
                  {
                    title: t('dashboard.submitted-date'),
                    dataIndex: 'submittedDate',
                    key: 'submittedDate',
                    defaultSortOrder: 'descend',
                    sorter: (a: any, b: any) => {
                      const dateA = a.log?.submissionDate
                        ? dayjs(a.log.submissionDate).valueOf()
                        : 0;
                      const dateB = b.log?.submissionDate
                        ? dayjs(b.log.submissionDate).valueOf()
                        : 0;
                      return dateA - dateB;
                    },
                    sortIcon: renderSortIcon,
                    render: (text: string) => (
                      <Text style={{ fontSize: '12px', color: 'var(--color-text-dark, #042a2a)' }}>
                        {text}
                      </Text>
                    ),
                  },
                  {
                    title: t('main.period'),
                    dataIndex: 'period',
                    key: 'period',
                    sorter: (a: any, b: any) => {
                      const dateA = a.log?.fromReportingDate
                        ? dayjs(a.log.fromReportingDate).valueOf()
                        : 0;
                      const dateB = b.log?.fromReportingDate
                        ? dayjs(b.log.fromReportingDate).valueOf()
                        : 0;
                      return dateA - dateB;
                    },
                    sortIcon: renderSortIcon,
                    render: (text: string) => (
                      <Text style={{ fontSize: '12px', color: 'var(--color-text-dark, #042a2a)' }}>
                        {text}
                      </Text>
                    ),
                  },
                  {
                    title: t('main.action-required'),
                    key: 'action',
                    render: (_: any, record: any) => {
                      const handleOpenSubmission = () => {
                        if (
                          record.connectionId &&
                          record.mappingId &&
                          record.dataLogId
                        ) {
                          openSubmission(
                            record.connectionId,
                            record.mappingId,
                            record.dataLogId
                          );
                        }
                      };
                      return (
                        <ScopeButton
                          width="70px"
                          height="25px"
                          fontSize="12px"
                          lineheight="12px"
                          onClick={handleOpenSubmission}
                          type="primary"
                          disabled={
                            !record.connectionId ||
                            !record.mappingId ||
                            !record.dataLogId
                          }
                        >
                          {getReturnActionLabel(
                            { status: DataLogStatus.SUBMITTED },
                            t
                          )}
                        </ScopeButton>
                      );
                    },
                  },
                ]}
                pagination={true}
                rowsPerPage={capturedSubmissionsPageSize}
                enableColumnsCustomization={false}
                rowHeight={40}
                fontSize={12}
                headerFontSize={12}
                showSizeChanger={true}
                getNewPageSize={saveCapturedSubmissionsPageSize}
                transactionType=""
                reportId=""
              />
            </div>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Text style={{ color: 'var(--color-text-muted)' }}>
            {t('dashboard.no-submitted-returns-for-period')}
          </Text>
        </div>
      )}
    </BaseCard>
  );
};
